import type { FormBrandOverride, FormConsentMode, SurveyAudienceMode, SurveyDedupeKey, SurveyResponseLimit } from '@engage/types';
import { INPUT_STYLE, Row, Section, Toggle } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';
import { useSurveyBuilderStore } from './store';
import { ImageUploadField } from '../../../components/ui/ImageUploadField';

/**
 * Right-pane "Survey" tab — survey-level settings: audience mode, response
 * limit + dedupe key, consent, reminder schedule (identified surveys), and
 * slug. Reads/writes the builder store directly.
 */
export function SurveySettings() {
  const s = useSurveyBuilderStore();

  return (
    <div style={{ fontFamily: FORM_UI_FONT }}>
      <Section title="AUDIENCE" note="Anonymous = open public link. Identified = tokenised links tied to a known contact/candidate.">
        <SegToggle<SurveyAudienceMode>
          value={s.audienceMode}
          onChange={s.setAudienceMode}
          options={[
            { value: 'ANONYMOUS', label: 'Anonymous' },
            { value: 'IDENTIFIED', label: 'Identified' },
          ]}
        />
      </Section>

      <Section title="RESPONSE LIMIT" note="One response per respondent uses the dedupe key below (best-effort on open links).">
        <SegToggle<SurveyResponseLimit>
          value={s.responseLimit}
          onChange={s.setResponseLimit}
          options={[
            { value: 'MANY', label: 'Unlimited' },
            { value: 'ONE', label: 'One per person' },
          ]}
        />
        {s.responseLimit === 'ONE' && (
          <div style={{ marginTop: 12 }}>
            <Row label="DEDUPE KEY">
              <select
                value={s.dedupeKey}
                onChange={(e) => s.setDedupeKey(e.target.value as SurveyDedupeKey)}
                style={INPUT_STYLE}
              >
                <option value="NONE">None</option>
                <option value="EMAIL">Email</option>
                <option value="PHONE">Phone</option>
                {s.audienceMode === 'IDENTIFIED' && <option value="TOKEN">One per token</option>}
              </select>
            </Row>
          </div>
        )}
      </Section>

      <Section title="BRANDING" note="Shown on every page of the hosted survey. Overrides your organisation's BrandKit; blank fields fall back to it.">
        <BrandingControls override={s.brandOverride} onChange={s.setBrandOverride} />
      </Section>

      <Section title="CONSENT">
        <Row label="MODE">
          <select
            value={s.consentMode ?? 'DISABLED'}
            onChange={(e) => {
              const mode = e.target.value as FormConsentMode;
              s.setConsent(mode === 'DISABLED' ? null : mode, s.consentText);
            }}
            style={INPUT_STYLE}
          >
            <option value="DISABLED">Disabled</option>
            <option value="NOTICE">Notice (display only)</option>
            <option value="CHECKBOX">Checkbox (must accept)</option>
          </select>
        </Row>
        {s.consentMode && s.consentMode !== 'DISABLED' && (
          <Row label="CONSENT TEXT">
            <textarea
              value={s.consentText ?? ''}
              onChange={(e) => s.setConsent(s.consentMode, e.target.value)}
              rows={3}
              style={{ ...INPUT_STYLE, resize: 'vertical' }}
            />
          </Row>
        )}
      </Section>

      {s.audienceMode === 'IDENTIFIED' && (
        <Section
          title="REMINDERS"
          note="Nudge incomplete identified responses. Offsets are hours from last activity."
        >
          <Row label="OFFSETS (HOURS, COMMA-SEPARATED)">
            <input
              value={s.reminderOffsets.join(', ')}
              onChange={(e) => s.setReminders(parseOffsets(e.target.value), s.maxReminders)}
              placeholder="24, 72"
              style={INPUT_STYLE}
            />
          </Row>
          <Row label="MAX REMINDERS">
            <input
              type="number"
              min={0}
              max={10}
              value={s.maxReminders}
              onChange={(e) => s.setReminders(s.reminderOffsets, clamp(Number(e.target.value), 0, 10))}
              style={INPUT_STYLE}
            />
          </Row>
          <Toggle
            on={s.reminderOffsets.length > 0 && s.maxReminders > 0}
            label="Reminders active"
            sub="Turn off by setting max reminders to 0."
            onChange={(on) => s.setReminders(s.reminderOffsets, on ? Math.max(1, s.maxReminders) : 0)}
          />
        </Section>
      )}

      <Section title="HOSTED LINK" note="Used in the public survey URL, so it must be unique within your organisation.">
        <Row label="SLUG">
          <input
            value={s.slug}
            onChange={(e) => s.setSlug(e.target.value)}
            style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          />
        </Row>
      </Section>
    </div>
  );
}

function SegToggle<T extends string>(props: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {props.options.map((o) => {
        const active = o.value === props.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => props.onChange(o.value)}
            style={{
              flex: 1,
              padding: '9px 10px',
              borderRadius: 9,
              border: `1.5px solid ${active ? C.indigo : C.line}`,
              background: active ? C.candidateBg : '#fff',
              color: active ? C.indigo : C.sub,
              fontWeight: active ? 700 : 500,
              fontSize: 12.5,
              cursor: 'pointer',
              fontFamily: FORM_UI_FONT,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function BrandingControls(props: {
  override: FormBrandOverride | null;
  onChange: (next: FormBrandOverride | null) => void;
}) {
  const o = props.override ?? {};
  const patch = (p: Partial<FormBrandOverride>) => {
    const next = { ...o, ...p };
    // Drop empty keys so an all-blank override collapses back to null (pure
    // BrandKit inheritance).
    const cleaned: FormBrandOverride = {};
    for (const [k, v] of Object.entries(next)) {
      if (v !== undefined && v !== null && v !== '') (cleaned as Record<string, unknown>)[k] = v;
    }
    props.onChange(Object.keys(cleaned).length ? cleaned : null);
  };

  return (
    <div>
      <Row label="LOGO" hint="Displayed at the top of every survey page.">
        <ImageUploadField
          value={o.logo_url}
          onChange={(url) => patch({ logo_url: url ?? undefined })}
          inputStyle={INPUT_STYLE}
          testId="survey-logo"
        />
      </Row>

      <div style={{ display: 'flex', gap: 10 }}>
        <ColorField label="PRIMARY" value={o.primary_color} fallback="#1e1b4b" onChange={(v) => patch({ primary_color: v })} />
        <ColorField label="ACCENT" value={o.accent_color} fallback="#3b5bdb" onChange={(v) => patch({ accent_color: v })} />
        <ColorField label="BACKGROUND" value={o.background_color} fallback="#f4f5f9" onChange={(v) => patch({ background_color: v })} />
      </div>
    </div>
  );
}

function ColorField(props: {
  label: string;
  value: string | undefined;
  fallback: string;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <div style={{ flex: 1 }}>
      <label style={{ fontSize: 10.5, fontWeight: 700, color: C.indigoSoft, display: 'block', marginBottom: 5 }}>{props.label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="color"
          value={props.value ?? props.fallback}
          onChange={(e) => props.onChange(e.target.value)}
          style={{ width: 34, height: 30, padding: 0, border: `1px solid ${C.line}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        />
        {props.value ? (
          <button
            type="button"
            onClick={() => props.onChange(undefined)}
            title="Reset to BrandKit"
            style={{ fontSize: 10.5, color: C.faint, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: FORM_UI_FONT, padding: 0 }}
          >
            reset
          </button>
        ) : null}
      </div>
    </div>
  );
}

function parseOffsets(raw: string): number[] {
  return raw
    .split(',')
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isFinite(n) && n >= 1)
    .slice(0, 10);
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}
